import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { RefreshCw, Save } from 'lucide-react';
import api from '../api';

const StripeCardForm = ({ userCpf, onSuccess, onCancel, onFailure }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isSaving, setIsSaving] = useState(false);
    const [cpfValue, setCpfValue] = useState('');

    // Format CPF (000.000.000-00)
    const formatarCPF = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
            .substring(0, 14);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Evita cliques duplos se já estiver salvando
        if (!stripe || !elements || isSaving) {
            return;
        }

        if (!userCpf && !cpfValue.trim()) {
            toast.error("O CPF é obrigatório para cadastrar um cartão.");
            return;
        }

        setIsSaving(true);
        const loadToast = toast.loading("Validando e criptografando cartão no Stripe...");

        try {
            // 2. Dispara a confirmação no Stripe
            const result = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required'
            });

            // 3. SE DER ERRO: Mas o erro for porque JÁ DEU CERTO (o seu caso atual!)
            if (result.error) {
                if (result.error.code === 'setup_intent_unexpected_state' || result.error.message.includes('already succeeded')) {
                    // O Stripe reclamou, mas é porque o cartão já foi salvo! Vamos pegar o ID que já veio no erro ou na intenção
                    const paymentMethodId = result.error.setup_intent?.payment_method || result.setupIntent?.payment_method;

                    if (paymentMethodId) {
                        await salvarNoBackend(paymentMethodId, loadToast);
                        return;
                    }
                }

                // Se for um erro real (cartão recusado, vencido, etc.)
                toast.error(result.error.message || "Erro ao validar cartão.", { id: loadToast });
                setIsSaving(false);
                return;
            }

            // 4. SE DER CERTO DE PRIMEIRA:
            if (result.setupIntent && result.setupIntent.status === 'succeeded') {
                const paymentMethodId = result.setupIntent.payment_method;
                await salvarNoBackend(paymentMethodId, loadToast);
            }

        } catch (err) {
            console.error(err);
            toast.error("Erro interno ao processar pagamento.", { id: loadToast });
            setIsSaving(false);
        }
    };

    // Função auxiliar criada para não duplicar código de envio pro seu banco
    const salvarNoBackend = async (paymentMethodId, loadToast) => {
        try {
            toast.loading("Vinculando cartão à sua conta...", { id: loadToast });

            // Envia o ID para a sua rota do Express (com Sequelize)
            await api.post('/payment/cards', { 
                paymentMethodId,
                cpf: !userCpf ? cpfValue.replace(/\D/g, '') : undefined
            });

            toast.success("Cartão cadastrado com sucesso!", { id: loadToast });
            onSuccess(); // Fecha o formulário e atualiza a lista
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Cartão validado no Stripe, mas falhou ao salvar no sistema local.", { id: loadToast });
            setIsSaving(false);
            if (onFailure) {
                onFailure(); // Regenera o SetupIntent para limpar o estado consumido
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {!userCpf && (
                <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>
                        CPF do Titular do Cartão (Obrigatório)
                    </label>
                    <input
                        type="text"
                        placeholder="000.000.000-00"
                        value={cpfValue}
                        onChange={(e) => setCpfValue(formatarCPF(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            borderRadius: '10px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                        required
                    />
                </div>
            )}

            {/* Componente oficial do Stripe que substitui aquele texto fixo */}
            <PaymentElement options={{ layout: 'tabs' }} />

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                    type="submit"
                    disabled={isSaving || !stripe}
                    style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        backgroundColor: '#ee4d2d', color: 'white', border: 'none', padding: '12px',
                        borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: isSaving ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isSaving ? <RefreshCw size={16} className="custom-spin" /> : <Save size={16} />}
                    {isSaving ? 'Salvando...' : 'Salvar Cartão com Segurança'}
                </button>

                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSaving}
                    style={{
                        padding: '12px 20px', background: '#f1f5f9', border: 'none', borderRadius: '8px',
                        cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#475569'
                    }}
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
};

export default StripeCardForm;